/**
 * Which board tab is on screen — read from the URL, never from state.
 *
 * The tab lives in the path (`/ta-action-boards/:tab`) for two reasons: a filtered board is worth
 * linking, and `useState` does not survive the 15-minute idle reload (see `useAutoRefresh`), so a
 * recruiter who had opened Scheduling would silently be returned to Feedback.
 *
 * Two rules it owns:
 *
 *  · An unknown or missing segment is canonicalised to `/feedback` WITHOUT adding a history entry —
 *    the bare path is kept working for old bookmarks.
 *  · **Requests is only ever the caller's own board.** It reads the signed-in recruiter's own
 *    mailbox, so on a colleague's board the tab is hidden and the `/requests` URL falls back to
 *    Feedback rather than rendering an empty inbox that looks like theirs.
 */
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isTabId, type TabId } from './BoardChrome';

export function useBoardTab(isOwnBoard: boolean): {
  /** The tab named by the URL. */
  tab: TabId;
  /** What to render — `tab`, unless it is Requests on someone else's board. */
  effectiveTab: TabId;
  setTab: (next: TabId) => void;
} {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const tab: TabId = isTabId(tabParam) ? tabParam : 'feedback';

  const setTab = useCallback(
    (next: TabId) => navigate(`/ta-action-boards/${next}`, { replace: false }),
    [navigate]
  );

  useEffect(() => {
    if (!isTabId(tabParam)) navigate('/ta-action-boards/feedback', { replace: true });
  }, [tabParam, navigate]);

  return { tab, effectiveTab: tab === 'requests' && !isOwnBoard ? 'feedback' : tab, setTab };
}
